import { ALL_DATES } from '@utils/cbDateSettings'

/**
 * CC-50013 G2 — the bridge that lets a SPECIALIZED report page render its filter
 * bar through `ReportFilterPanel`, the same metadata-driven registry the 119
 * generic reports use.
 *
 * ## Why a bridge and not a straight swap
 *
 * `ReportFilterPanel` is driven by a CB report DEFINITION: an array of columns
 * carrying `columnId` / `filterTypeName` / `filterConditions`, with values keyed
 * by `columnId`. Specialized reports have no such definition. Their filters are
 * the fields of a bespoke C# filter class (`TimeEntriesReportFilter`,
 * `RevenueAndCommissionsReportFilter`, …) and the page's state IS the wire
 * object — often with one control writing TWO keys (`clientId` + `clientName`).
 *
 * That mismatch is the whole risk. CB's Newtonsoft deserialization **silently
 * ignores unknown JSON properties**: rename or drop a filter key and it binds to
 * nothing, the stored proc runs UNSCOPED, and nothing errors — not the screen,
 * not the logs, not the tests. It has bitten this project at least four times
 * (CC-49833, both halves of CC-50020). So the mapping between "what the widget
 * emits" and "what goes on the wire" is made EXPLICIT here, per field, and
 * pinned by per-page wire-contract tests, rather than being implied by a shared
 * component's internals.
 *
 * ## The contract
 *
 * A field spec is `{ key, label, filterTypeName, get, set, …widgetProps }`:
 *   - `key`    — the synthetic `columnId` the widget is addressed by. Never sent.
 *   - `get(filters)`            → the string the widget wants.
 *   - `set(widgetValue, filters)` → a PARTIAL of the page's wire object.
 *
 * `set` returns a partial (not a whole object) so a control can write several
 * wire keys at once and leave everything else — including keys no control
 * exposes, like `billableObjectType` or `billingPartnerFieldId` — untouched.
 * Those invisible keys are load-bearing; several of them are the difference
 * between a scoped and an unscoped proc.
 */

export const ALL = ALL_DATES

/** Split CB's "id:name" lookup format on the FIRST colon — names can contain colons. */
const splitIdName = (v) => {
  const s = String(v ?? '')
  if (!s) return { id: '', name: '' }
  const i = s.indexOf(':')
  return i === -1 ? { id: s, name: '' } : { id: s.slice(0, i), name: s.slice(i + 1) }
}

/**
 * A single-object picker (client / matter / timekeeper / vendor …) mapped onto a
 * pair of wire keys.
 *
 * `LookupFilter` stores "id:name" so the label survives a remount (CC-49866);
 * the page keeps the id and the name in separate wire fields because that is
 * what the C# filter class declares. `nameKey` is optional — a few filter
 * classes carry only the id.
 *
 * `emptyId` matters: most classes declare `Guid? clientId` and want `null` when
 * nothing is picked, but a couple declare `string userIds` and want `''`.
 * Sending `''` where `Guid?` is expected does not bind, which is the silent
 * failure mode above — so the caller states it.
 */
export const objectField = ({
  key,
  label,
  typeId,
  idKey,
  nameKey,
  emptyId = null,
  multi = false,
}) => ({
  key,
  label,
  filterTypeName: multi ? 'TAGMANAGER' : 'LOOKUP',
  // LookupFilter reads its target object type from filterConditions[0].key
  // (matching legacy Report.cshtml line 430).
  filterConditions: [{ key: String(typeId) }],
  get: (f) => {
    const id = f?.[idKey]
    if (id == null || id === '') return ''
    const name = nameKey ? f?.[nameKey] : ''
    return name ? `${id}:${name}` : String(id)
  },
  set: (v) => {
    const { id, name } = splitIdName(v)
    const out = { [idKey]: id || emptyId }
    if (nameKey) out[nameKey] = name || ''
    return out
  },
})

/**
 * A MULTI-value object picker whose widget string is stored verbatim on one key.
 *
 * `TagManagerFilter` stores `"id:name|id2:name2"`, and the pages that use it keep
 * exactly that string in state and translate it at wire time (Matter Metrics
 * pipe-joins the ids with a trailing pipe). So unlike `objectField` there is no
 * id/name pair to split — the value passes through untouched and the page's own
 * wire function owns the encoding. Keeping the translation there is what stops
 * this refactor changing the wire.
 */
export const multiObjectField = ({ key, label, typeId, valueKey }) => ({
  key,
  label,
  filterTypeName: 'TAGMANAGER',
  filterConditions: [{ key: String(typeId) }],
  get: (f) => f?.[valueKey] || '',
  set: (v) => ({ [valueKey]: v || '' }),
})

/**
 * A from/to date range mapped onto two wire keys.
 *
 * `DateFilter` encodes "YYYY-MM-DD|YYYY-MM-DD", or the `$$cb_all` sentinel for
 * an unbounded range. Specialized filter classes take two nullable DateTimes
 * instead, so All / empty maps to `emptyValue` on both sides.
 *
 * `emptyValue` defaults to `''`. Pass `null` where the class declares
 * `DateTime?` and an empty string would fail to bind (CC-49949 pinned exactly
 * this for Revenue & Commissions).
 *
 * `omitAll` suppresses the panel's "Clear / All Dates" affordance. Use it on any
 * report where an empty range does NOT mean "no date filter" — Fee Receipts is
 * the cautionary case: CB substitutes 1753-01-01 → today for null payment
 * dates, so a cleared range silently produces an ALL-TIME run whose figures are
 * wrong rather than merely unfiltered (CC-50020). Clearing both inputs was
 * already possible there; this just declines to advertise it.
 *
 * `omitPresets` (optional, preset keys from cbDateSettings' DATE_RANGE_PRESET_GROUPS)
 * drops further quick-range presets from the panel's select, for a report on which
 * they can never run — spec 214's Advanced Report range cannot run with an open end,
 * so "Future" and "Today & Future" are not offered there. Carried onto the column
 * (through `columnProps`, which `toFilterColumns` spreads) only when given, so every
 * other report's column — and so its presets — is untouched.
 */
export const dateRangeField = ({
  key,
  label,
  startKey,
  endKey,
  emptyValue = '',
  omitAll = false,
  omitPresets,
}) => ({
  key,
  label,
  filterTypeName: 'DATE',
  omitAll,
  ...(omitPresets ? { omitPresets, columnProps: { omitPresets } } : {}),
  get: (f) => {
    const from = f?.[startKey] || ''
    const to = f?.[endKey] || ''
    return from || to ? `${from}|${to}` : ALL
  },
  set: (v) => {
    if (!v || v === ALL) return { [startKey]: emptyValue, [endKey]: emptyValue }
    const [from = '', to = ''] = String(v).split('|')
    return { [startKey]: from || emptyValue, [endKey]: to || emptyValue }
  },
})

/** A single date (as-of / statement date) mapped onto one wire key. */
export const dateField = ({ key, label, valueKey, emptyValue = '' }) => ({
  key,
  label,
  filterTypeName: 'SIMPLEDATE',
  get: (f) => f?.[valueKey] || '',
  set: (v) => ({ [valueKey]: v || emptyValue }),
})

/**
 * A mutually-exclusive choice (Both/Billed/Unbilled, Posted tri-state, …).
 *
 * Rendered by `RadioFilter` — SINGLE-select, because these option sets are
 * mutually exclusive and multi-checking them is meaningless (CC-49861 fixed
 * exactly that on the generic side). `options` are `{ value, label }` with
 * `value` a STRING; `decode` turns the chosen string back into whatever the
 * wire wants (an int, a nullable bool, …) and `encode` does the reverse.
 *
 * Every option is declared, including the "all"/"don't filter" one, so the page
 * keeps the exact wording legacy uses instead of inheriting a generic "All".
 */
export const choiceField = ({
  key,
  label,
  valueKey,
  options,
  encode = (v) => (v == null ? '' : String(v)),
  decode = (s) => s,
}) => ({
  key,
  label,
  filterTypeName: 'RADIO',
  filterConditions: options.map((o) => ({ key: String(o.value), value: o.label })),
  get: (f) => encode(f?.[valueKey]),
  set: (v) => ({ [valueKey]: decode(v) }),
})

/** A plain number (year, threshold). `NUMBER` would bring operator semantics
 *  the specialized classes have no field for, so this stays a text input the
 *  page parses — same control the bespoke bars used. */
export const numberField = ({ key, label, valueKey, fallback = '' }) => ({
  key,
  label,
  filterTypeName: 'TEXT',
  get: (f) => (f?.[valueKey] == null ? '' : String(f[valueKey])),
  set: (v) => {
    const n = parseInt(String(v).replace(/[^\d-]/g, ''), 10)
    return { [valueKey]: Number.isFinite(n) ? n : fallback }
  },
})

/**
 * A yes/no flag mapped onto a boolean wire key.
 *
 * NOTE this renders as a Yes/No SELECT (`BoolFilter`), not a checkbox. That is
 * deliberate on the generic side — CB core's makeFilters always posts
 * True/False and offers no "don't filter" state (CC-49925) — but it IS a visual
 * change for a page that used a checkbox, so prefer leaving genuine
 * display/mode toggles in the page's own action row instead of modelling them
 * as filters.
 */
export const boolField = ({ key, label, valueKey }) => ({
  key,
  label,
  filterTypeName: 'BOOL',
  get: (f) => (f?.[valueKey] ? '1' : '0'),
  set: (v) => ({ [valueKey]: v === '1' }),
})

/**
 * A fixed or server-loaded option list, multi-select with "All" semantics.
 * `options` are `{ value, label }`; `join`/`split` control the wire encoding
 * (CB's list filters are usually pipe-delimited, sometimes with a trailing pipe
 * — state it rather than inherit it).
 */
export const listField = ({
  key,
  label,
  valueKey,
  options = [],
  join = (vals) => vals.join('|'),
  split = (s) => String(s || '').split('|').filter(Boolean),
  // CC-53890: where an empty selection MEANS "all" (the GL's Account Types:
  // no type chosen = every account), show it as "All" instead of the
  // "None selected" DropdownFilter otherwise reads for ''. Wire unchanged.
  emptyIsAll = false,
  // CC-53890: a partial merged into every change that CHOOSES something — how
  // one control clears a sibling it must never be combined with (see
  // accountsField). NOT merged when the field is emptied or set to All: that
  // chooses no scope of its own, so it has no business discarding the
  // sibling's. (On the GL, unticking Account Types' "All" row — a no-op on that
  // control, since empty reads as All — used to wipe a specific-account pick
  // and re-run the ledger for every account.)
  alsoSet = null,
}) => ({
  key,
  label,
  filterTypeName: 'DROPDOWN',
  filterConditions: options.map((o) => ({ key: String(o.value), value: o.label })),
  get: (f) => {
    const vals = split(f?.[valueKey])
    if (vals.length === 0) return emptyIsAll ? ALL : ''
    return vals.join('|')
  },
  set: (v) => {
    if (!v || v === ALL) return { [valueKey]: join([]) }
    const vals = String(v).split('|').filter(Boolean)
    if (vals.length === 0) return { [valueKey]: join([]) }
    return { [valueKey]: join(vals), ...(alsoSet || {}) }
  },
})

const byAccountId = (a, b) => Number(a) - Number(b) || String(a).localeCompare(String(b))

/**
 * CC-53890 (spec 128) — a picker of SPECIFIC accounts, rendered by the shared
 * ACCOUNTSDROPDOWN control (DropdownFilter + accountPickerOptions).
 *
 * Its page-state value has THREE states, and the difference between two of them
 * is money:
 *
 *   ALL ($$cb_all)  every account — the default. The page sends no list.
 *   ''              NONE selected. The page REFUSES to run. It must never be
 *                   sent: CB reads an empty `filteredAccounts` as ALL accounts
 *                   (measured), so '' on the wire would be "nothing" on screen
 *                   and "everything" in the figures.
 *   'id|id'         these accounts, explicit ids — frozen by design ("these
 *                   two"), unlike an account TYPE, which re-resolves each run.
 *
 * `set` returns a PARTIAL of the wire object (the bridge's contract): picking
 * specific accounts also writes `clears` — on the GL, `{ accountCategories: '' }`
 * — because the page never sends a type token AND a list together. Ids are
 * sorted numerically so one selection is one wire string (the job layer dedupes
 * on it), and are otherwise passed through untouched: an id that no longer
 * exists stays in the selection, where the page can SAY so. Stripping it could
 * empty the list, and an empty list is the all-accounts hazard above.
 */
export const accountsField = ({ key, label, valueKey, clears = {}, help }) => ({
  key,
  label,
  filterTypeName: 'ACCOUNTSDROPDOWN',
  filterConditions: [],
  // Column props the shared control reads (ReportFilterPanel DropdownFilter).
  columnProps: {
    selectSubAccounts: true,
    noneLabel: 'None selected',
    clearValue: ALL,
    ...(help ? { help } : {}),
  },
  get: (f) => {
    const v = f?.[valueKey]
    if (v == null || v === ALL) return ALL
    return String(v)
  },
  set: (v) => {
    if (v == null || v === ALL) return { [valueKey]: ALL }
    const ids = [...new Set(String(v).split('|').filter(Boolean))].sort(byAccountId)
    if (ids.length === 0) return { [valueKey]: '' }
    return { [valueKey]: ids.join('|'), ...clears }
  },
})

/**
 * Project the field specs into the column metadata `ReportFilterPanel` and
 * `QueueReportModal` consume. `filterOrder` follows declaration order, so the
 * bar reads in the order the page listed its filters.
 */
export const toFilterColumns = (fields = []) =>
  fields.map((f, i) => ({
    columnId: f.key,
    label: f.label,
    filterTypeName: f.filterTypeName,
    filterConditions: f.filterConditions,
    filterable: true,
    filterOrder: i,
    ...(f.omitAll ? { omitAll: true } : {}),
    // CC-53890: extra props a field hands the shared control (accountsField).
    ...(f.columnProps || {}),
  }))

/** Project the page's wire object into widget values keyed by synthetic columnId. */
export const toFilterValues = (fields = [], filters = {}) => {
  const out = {}
  for (const f of fields) out[f.key] = f.get(filters)
  return out
}

/**
 * Apply one widget change, returning the NEXT whole wire object.
 *
 * Spreads the partial from `set` over the previous filters, so wire keys with no
 * control keep their values. An unknown key is a no-op rather than a throw: the
 * queue modal can outlive a page's field list across a code change, and dropping
 * an unrecognised control's value is strictly better than crashing the modal.
 */
export const applyFilterChange = (fields = [], columnId, value, filters = {}) => {
  const field = fields.find((f) => f.key === columnId)
  if (!field) return filters
  return { ...filters, ...field.set(value, filters) }
}

/**
 * Reset every field this bar owns back to `defaults`, leaving wire keys no
 * control exposes exactly as they are. Used by the panel's Reset button, which
 * no specialized page offered before.
 */
export const resetFilterFields = (fields = [], filters = {}, defaults = {}) => {
  let next = { ...filters }
  for (const f of fields) next = { ...next, ...f.set(f.get(defaults), next) }
  return next
}
